import { module as BaseModule } from "modujs";

const DEFAULT_STATUS = {
  open: "Open Now",
  closed: "Closed",
};

const parseMinutesFromTime = (time) => {
  if (!time || typeof time !== "string") {
    return null;
  }

  const [hours, minutes] = time.split(":").map((value) => parseInt(value, 10));

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
};

const getTimePartsForTimezone = (timeZone) => {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "long",
  });

  const parts = formatter.formatToParts(now).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});

  return {
    weekday: (parts.weekday || "").toLowerCase(),
    minutes: parseMinutesFromTime(`${parts.hour}:${parts.minute}`),
  };
};

const decodeHtmlEntities = (value) => {
  if (!value || typeof value !== "string") {
    return value;
  }

  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
};

export default class BusinessStatus extends BaseModule {
  init() {
    this.statusEl = this.el;

    if (!this.statusEl) {
      return;
    }

    this.hours = this.parseHours();
    this.timeZone =
      this.statusEl.getAttribute("data-business-status-timezone") ||
      "America/Detroit";

    this.updateStatus();
    this.startPolling();
  }

  parseHours() {
    if (!this.statusEl) {
      return [];
    }

    const rawAttr =
      this.statusEl.dataset.businessStatusHours ||
      this.statusEl.getAttribute("data-business-status-hours");
    const raw = decodeHtmlEntities(rawAttr);

    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map((item) => ({
          day: typeof item.day === "string" ? item.day.toLowerCase() : null,
          label: item.label || null,
          open: item.open || null,
          close: item.close || null,
          closed: Boolean(item.closed),
        }))
        .filter((item) => item.day);
    } catch (error) {
      console.warn("Failed to parse business status hours:", error);
      return [];
    }
  }

  getStatusForNow() {
    if (!this.hours || this.hours.length === 0) {
      return { isOpen: false };
    }

    const { weekday, minutes } = getTimePartsForTimezone(this.timeZone);

    if (!weekday || minutes === null) {
      return { isOpen: false };
    }

    const today = this.hours.find((item) => item.day === weekday);

    if (!today) {
      return { isOpen: false };
    }

    if (today.closed) {
      return { isOpen: false };
    }

    const openMinutes = parseMinutesFromTime(today.open);
    const closeMinutes = parseMinutesFromTime(today.close);

    if (openMinutes === null || closeMinutes === null) {
      return { isOpen: false };
    }

    if (closeMinutes <= openMinutes) {
      return { isOpen: false };
    }

    const isOpen = minutes >= openMinutes && minutes < closeMinutes;

    return {
      isOpen,
      debug: {
        weekday,
        minutes,
        openMinutes,
        closeMinutes,
      },
    };
  }

  updateStatus() {
    const status = this.getStatusForNow();
    const nextText = status.isOpen ? DEFAULT_STATUS.open : "";

    if (!this.statusEl) {
      return;
    }

    if (this.statusEl.textContent !== nextText) {
      this.statusEl.textContent = nextText;
    }

    if (status.isOpen) {
      this.statusEl.hidden = false;
      this.statusEl.style.removeProperty("display");
      this.statusEl.setAttribute("aria-hidden", "false");
    } else {
      this.statusEl.hidden = true;
      this.statusEl.style.setProperty("display", "none");
      this.statusEl.setAttribute("aria-hidden", "true");
    }

    this.statusEl.setAttribute(
      "data-business-status-state",
      status.isOpen ? "open" : "closed"
    );

    if (status.debug) {
      let isDev = false;

      try {
        isDev = Boolean(import.meta?.env?.DEV);
      } catch (error) {
        isDev = false;
      }

      if (isDev) {
        console.debug("[BusinessStatus]", status.debug);
      }
    }
  }

  startPolling() {
    this.stopPolling();

    if (!this.hours || this.hours.length === 0) {
      return;
    }

    this.pollInterval = window.setInterval(
      () => this.updateStatus(),
      60 * 1000
    );
  }

  stopPolling() {
    if (this.pollInterval) {
      window.clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  destroy() {
    this.stopPolling();
    this.statusEl = null;
  }
}
